import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { FiSettings, FiLogOut, FiUser, FiDownload, FiInfo } from 'react-icons/fi';

import { StoreState } from '../../../store/createStore';
import { logoutRequest } from '../../../store/modules/auth/actions';
import AuthTypesEnum from '../../../enums/authTypes';
import ToggleTheme from '../../SelectTheme';
import AboutModal from '../../AboutModal';
import { usePWA } from '../../../hooks/usePWA';
import { useSidebar } from '../SidebarContext';

import FlagBr from '../../../assets/images/signin/flag-br.svg';
import FlagUs from '../../../assets/images/signin/flag-us.svg';

import * as s from './styles';

// Language selector mini component
const LanguageSelector = (
  { onClose, isSidebarOpen }: { onClose: () => void; isSidebarOpen: boolean; }) => {
  const { i18n } = useTranslation();
  const languages = [
    { code: 'pt', label: 'Português', flag: FlagBr },
    { code: 'en', label: 'English', flag: FlagUs },
  ];

  const currentLangCode = i18n.language ? i18n.language.split('-')[0] : 'pt';

  return (
    <>
      {languages.map((lang) => (
        <s.MenuItem
          key={lang.code}
          onClick={() => {
            i18n.changeLanguage(lang.code);
            onClose();
          }}
          style={{
            fontWeight: currentLangCode === lang.code ? 600 : 400,
            justifyContent: isSidebarOpen ? 'flex-start' : 'center',
            padding: isSidebarOpen ? '0.625rem 0.75rem' : '0.625rem 0.25rem'
          }}
          title={!isSidebarOpen ? lang.label : ''}
        >
          <img src={lang.flag} alt={lang.label} width={16} height={12} />
          {isSidebarOpen && lang.label}
        </s.MenuItem>
      ))}
    </>
  );
};

const UserProfile = ({ onCustomizeMenu }: { onCustomizeMenu: () => void }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const { isInstallable, promptInstall } = usePWA();
  const { isSidebarOpen } = useSidebar();

  const { user, authType } = useSelector((state: StoreState) => state.auth);

  const handleLogout = async () => {
    if (authType === AuthTypesEnum.MICROSOFT) {
      try {
        await instance.logoutPopup({});
      } catch {
        // Ignore logout errors
      }
    }
    dispatch(logoutRequest());
  };

  const handleInstallApp = async () => {
    await promptInstall();
    setIsMenuOpen(false);
  };

  const handleOpenAbout = () => {
    setIsAboutOpen(true);
    setIsMenuOpen(false);
  };

  const userName = user?.name || 'User';
  const userEmail = user?.email || '';

  // Obtem as iniciais do usuário para o avatar
  const getUserInitials = () => {
    const parts = userName.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return userName.substring(0, 2).toUpperCase();
  };

  // Estilo comum para itens quando sidebar fechado
  const menuItemStyle = {
    justifyContent: isSidebarOpen ? 'flex-start' : 'center',
    padding: isSidebarOpen ? '0.625rem 0.75rem' : '0.625rem 0.25rem'
  };

  return (
    <s.Container>
      <s.ProfileButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
        <s.AvatarWrapper>
          <s.Avatar>
            {isSidebarOpen ? getUserInitials() : <FiUser size={18} />}
          </s.Avatar>

        </s.AvatarWrapper>
        {isSidebarOpen && (
          <>
            <s.UserInfo>
              <s.UserName title={userName}>{userName}</s.UserName>
              {userEmail && <s.UserEmail title={userEmail}>{userEmail}</s.UserEmail>}
            </s.UserInfo>
            <s.SettingsIcon>
              <FiSettings size={18} />
            </s.SettingsIcon>
          </>
        )}
      </s.ProfileButton>

      {isMenuOpen && (
        <>
          <s.MenuOverlay onClick={() => setIsMenuOpen(false)} />
          <s.Menu>
            <s.MenuSection>
              <s.ThemeRow
                onClick={(e) => e.stopPropagation()}
                style={{ justifyContent: isSidebarOpen ? 'flex-start' : 'center' }}
              >
                <ToggleTheme />
              </s.ThemeRow>
            </s.MenuSection>

            <s.MenuSection>
              {isSidebarOpen && <s.MenuLabel>{t('language')}</s.MenuLabel>}
              <LanguageSelector
                onClose={() => setIsMenuOpen(false)}
                isSidebarOpen={isSidebarOpen}
              />
            </s.MenuSection>

            <s.MenuSection>
              <s.MenuItem
                onClick={() => { setIsMenuOpen(false); onCustomizeMenu(); }}
                style={menuItemStyle}
                title={t('menuCustomization.title')}
              >
                <FiSettings size={16} />
                {isSidebarOpen && t('menuCustomization.title')}
              </s.MenuItem>
            </s.MenuSection>

            {isInstallable && (
              <s.MenuSection>
                <s.MenuItem
                  onClick={handleInstallApp}
                  style={menuItemStyle}
                  title={!isSidebarOpen ? t('installAsApp') : ''}
                >
                  <FiDownload size={16} />
                  {isSidebarOpen && t('installAsApp')}
                </s.MenuItem>
              </s.MenuSection>
            )}

            <s.MenuSection>
              <s.MenuItem
                onClick={handleOpenAbout}
                style={menuItemStyle}
                title={!isSidebarOpen ? t('about') : ''}
              >
                <FiInfo size={16} />
                {isSidebarOpen && t('about')}
              </s.MenuItem>
            </s.MenuSection>

            <s.MenuSection>
              <s.MenuItemDanger
                onClick={handleLogout}
                style={menuItemStyle}
                title={!isSidebarOpen ? t('logout') : ''}
              >
                <FiLogOut size={16} />
                {isSidebarOpen && t('logout')}
              </s.MenuItemDanger>
            </s.MenuSection>
          </s.Menu>
        </>
      )}

      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
    </s.Container>
  );
};

export default UserProfile;
